import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import { C, ff, t } from '../Talent/Dashboard/components/dashboardConstants';
import { getProducerById, recordProducerVisit } from '../../api/producerApi';
import { getCastingList } from '../../api/castingApi';
import { PRODUCER_PLACEHOLDER } from '../../shared/utils/profilePlaceholders';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { isDeadlineExpired } from '../../utils/castingDeadline';

export default function CompanyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('회사 소개');
  const [company, setCompany] = useState(null);
  const [castings, setCastings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const TABS = ['회사 소개', '진행 중인 캐스팅'];

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);

    Promise.all([
      getProducerById(id).catch(() => null),
      getCastingList({ producerProfileId: id, status: 'OPEN' }).catch(() => ({ content: [] })),
    ]).then(([profile, castingRes]) => {
      setCompany(profile);
      const list = castingRes?.content ?? (Array.isArray(castingRes) ? castingRes : []);
      setCastings(list);
      setIsLoading(false);
    });

    recordProducerVisit(id).catch(() => {});
  }, [id]);

  if (isLoading) {
    return (
      <PageLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 40px', display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  if (!company) {
    return (
      <PageLayout>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 40px', fontFamily: ff, textAlign: 'center' }}>
          <div style={t('20px', 700, C.bk)}>제작사 정보를 찾을 수 없습니다.</div>
          <button onClick={() => navigate(-1)} style={{ marginTop: '24px', padding: '12px 24px', borderRadius: '12px', border: 'none', background: C.bk, cursor: 'pointer', ...t('15px', 600, C.white) }}>
            이전으로
          </button>
        </div>
      </PageLayout>
    );
  }

  const specialties = Array.isArray(company.specialties)
    ? company.specialties
    : typeof company.specialties === 'string' && company.specialties
      ? company.specialties.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  return (
    <PageLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 40px 80px', fontFamily: ff }}>
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '32px', ...t('15px', 500, C.sub) }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          이전으로
        </button>

        {/* Company Card */}
        <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '24px', padding: '36px', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '28px' }}>
            {/* Logo */}
            <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: C.g200, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={company.logoUrl || PRODUCER_PLACEHOLDER}
                alt={company.companyName}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <h1 style={{ ...t('28px', 700, C.bk), margin: 0 }}>{company.companyName || company.name || '제작사'}</h1>
                {company.verified && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '8px', background: '#EFF6FF' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" fill={C.blue} /></svg>
                    <span style={t('12px', 600, C.blue)}>인증됨</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {company.address && <span style={t('13px', 400, C.sub)}>📍 {company.address}</span>}
                {company.website && <span style={t('13px', 400, C.sub)}>{company.website}</span>}
              </div>
            </div>
          </div>

          {/* Specialties + Contact */}
          {specialties.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {specialties.map(s => (
                <span key={s} style={{ padding: '6px 14px', borderRadius: '100px', border: `1px solid ${C.g200}`, ...t('13px', 500, C.bk) }}>{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ borderBottom: `1px solid ${C.g200}`, marginBottom: '28px', display: 'flex', gap: '0' }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{ padding: '12px 24px', background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab ? C.bk : 'transparent'}`, cursor: 'pointer', marginBottom: '-1px', ...t('15px', activeTab === tab ? 700 : 400, activeTab === tab ? C.bk : C.sub) }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === '회사 소개' && (
          <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '20px', padding: '32px' }}>
            <h2 style={{ ...t('20px', 700, C.bk), margin: '0 0 16px 0' }}>회사 소개</h2>
            <p style={{ ...t('15px', 400, C.bk), margin: 0, lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>
              {company.companyDescription || company.description || '소개 정보가 없습니다.'}
            </p>
          </div>
        )}

        {activeTab === '진행 중인 캐스팅' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {castings.length === 0 ? (
              <div style={{ ...t('15px', 400, C.sub), textAlign: 'center', padding: '48px 0' }}>진행 중인 캐스팅이 없습니다.</div>
            ) : (
              castings.map(casting => (
                <div key={casting.id} onClick={() => navigate(`/castings/${casting.id}`)}
                  style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '16px', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = C.shadow}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div>
                    <div style={t('17px', 600, C.bk)}>{casting.title}</div>
                    <div style={{ ...t('13px', 400, C.sub), marginTop: '6px' }}>마감 {casting.applicationDeadline || '미정'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {(() => {
                      const expired = casting.status === 'CLOSED' || isDeadlineExpired(casting.applicationDeadline);
                      return expired
                        ? <span style={{ padding: '5px 12px', borderRadius: '8px', background: '#F3F4F6', ...t('13px', 600, '#9CA3AF') }}>마감</span>
                        : <span style={{ padding: '5px 12px', borderRadius: '8px', background: '#DCFCE7', ...t('13px', 600, '#059669') }}>모집중</span>;
                    })()}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke={C.bk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
